import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { BaseParams, DetailsParams } from '@interfaces/app/nav.interfaces';
import { Invoice } from '@interfaces/invoice/invoice';
import { InvoiceDocument } from '@interfaces/invoice/invoiceDocument';
import { Observable, catchError, combineLatest, map, of, startWith, tap } from 'rxjs';
import { environment } from 'src/environments/environment';

import { fixInvoiceRows } from '@services/api.fixes';
import { NavService } from '@services/nav.service';
import { flattenResponses, getById, getOffsetDate } from '@services/utils';

@Injectable({ providedIn: 'root' })
export class InvoicesService {
  http = inject(HttpClient);
  navService = inject(NavService);
  enabledFeatures = toSignal(this.navService.enabledFeatures$);

  getInvoices(baseParams: BaseParams): Observable<Invoice[] | null> {
    return baseParams.sourceSystemCodes.includes('5')
      ? combineLatest([this.getSapInvoices(baseParams), this.getBikeInvoices(baseParams)]).pipe(
          tap((responses) => this.navService.toggleLoadingBar(responses)),
          map(flattenResponses),
        )
      : this.getBikeInvoices(baseParams);
  }

  getSapInvoices(baseParams: BaseParams): Observable<Invoice[] | null> {
    const { customerId, dateFrom, dateTo } = baseParams;
    const url = `${environment.invoiceApi}/invoices/customers/${customerId}`;
    const params = {
      sourceSystemId: 5,
      invoiceDateFrom: dateFrom,
      invoiceDateTo: dateTo,
    };
    return this.http.get<Invoice[]>(url, { params }).pipe(
      startWith(null),
      map((invoices) => fixInvoiceRows(invoices)),
      catchError(() => of([])),
    );
  }

  getBikeInvoices(baseParams: BaseParams): Observable<Invoice[] | null> {
    const { customerId, dateFrom, dateTo, sourceSystemCodes } = baseParams;
    const url = `${environment.invoiceApi}/invoices/customers/${customerId}`;

    const oneDay = 24 * 60 * 60 * 1000;
    const from = new Date(dateFrom).valueOf();
    const to = new Date(dateTo).valueOf();

    const diffDays = Math.round(Math.abs((from - to) / oneDay));
    const requests = sourceSystemCodes.includes('5') ? 1 : 4;
    const period = Math.round(diffDays / requests);

    const requests$ = [...Array(requests)].map((item, index) => {
      const params = {
        sourceSystemId: 1,
        invoiceDateFrom: getOffsetDate(period * (index + 1)),
        invoiceDateTo: getOffsetDate(period * index),
        InvoiceType: [],
      };
      return this.http.get<Invoice[]>(url, { params }).pipe(
        startWith(null),
        map((invoices) => fixInvoiceRows(invoices)),
        catchError(() => []),
      );
    });
    return combineLatest(requests$).pipe(map(flattenResponses));
  }

  getInvoiceById(detailsParams: DetailsParams): Observable<Invoice | null> {
    const { invoiceNumber, sourceSystemCode } = detailsParams;
    return this.getInvoices(detailsParams).pipe(
      map((invoices) =>
        getById(invoices || [], {
          invoiceNumber: parseInt(invoiceNumber),
          sourceSystemCode: parseInt(sourceSystemCode),
        }),
      ),
    );
  }

  getInvoiceDocument(
    customerId: string,
    invoiceNumber: string,
    sourceSystemCode: string,
  ): Observable<InvoiceDocument | null> {
    const url = `${environment.invoiceApi}/invoices/${invoiceNumber}/pdf/customers/${customerId}`;
    const params = {
      sourceSystemCode,
    };
    return this.http.get<InvoiceDocument>(url, { params }).pipe(startWith(null));
  }
}
